import { useEffect } from 'react';

interface MetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  locale?: string;
  author?: string;
  canonicalUrl?: string;
  robots?: string;
  structuredData?: any;
}

/**
 * Hook to dynamically update meta tags and page titles for SEO
 * Supports Open Graph, Twitter Card, and Structured Data
 */
export const useMetaTags = (props: MetaTagsProps) => {
  useEffect(() => {
    // Update Page Title
    document.title = props.title;

    // Update or create meta tags
    const updateOrCreateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
      }
      
      tag.setAttribute('content', content);
    };

    // Core Meta Tags
    updateOrCreateMetaTag('description', props.description);
    if (props.keywords) {
      updateOrCreateMetaTag('keywords', props.keywords);
    }
    if (props.author) {
      updateOrCreateMetaTag('author', props.author);
    }
    if (props.robots) {
      updateOrCreateMetaTag('robots', props.robots);
    }

    // Viewport and Charset (usually set once)
    if (!document.querySelector('meta[charset]')) {
      const charsetMeta = document.createElement('meta');
      charsetMeta.setAttribute('charset', 'UTF-8');
      document.head.insertBefore(charsetMeta, document.head.firstChild);
    }

    // Open Graph Meta Tags
    updateOrCreateMetaTag('og:title', props.title, true);
    updateOrCreateMetaTag('og:description', props.description, true);
    updateOrCreateMetaTag('og:type', props.type || 'website', true);
    if (props.locale) {
      updateOrCreateMetaTag('og:locale', props.locale, true);
    }
    if (props.image) {
      updateOrCreateMetaTag('og:image', props.image, true);
      updateOrCreateMetaTag('og:image:width', '1200', true);
      updateOrCreateMetaTag('og:image:height', '630', true);
    }
    if (props.url) {
      updateOrCreateMetaTag('og:url', props.url, true);
    }

    // Twitter Card Meta Tags
    updateOrCreateMetaTag('twitter:card', 'summary_large_image', true);
    updateOrCreateMetaTag('twitter:title', props.title, true);
    updateOrCreateMetaTag('twitter:description', props.description, true);
    if (props.image) {
      updateOrCreateMetaTag('twitter:image', props.image, true);
    }
    updateOrCreateMetaTag('twitter:site', '@bdlivecric', true);

    // Canonical URL
    if (props.canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = props.canonicalUrl;
    }

    // Structured Data (JSON-LD)
    if (props.structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(props.structuredData);
    }

    // Cleanup
    return () => {
      // Optional: You can add cleanup logic here if needed
    };
  }, [props]);
};

export default useMetaTags;
